import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  ReferenceArea,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import dayjs from 'dayjs'
import {
  PATTERNS,
  COLORS,
  MESSAGE_LOADING,
  MESSAGE_NO_DATA,
  MESSAGE_TIMERANGE,
  PRIORITY_VALUES,
} from './constants'
import TimeScaleSlider from './TimeScaleSlider'
import CustomTooltip from './CustomToolTip'
import { DataPointType, PatternTabPropsType, TimeType } from './types'
import { RangeValue } from 'rc-picker/lib/interface'
import enUS from 'antd/es/date-picker/locale/en_US'
import { DatePicker, message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import {
  getVisualizationData,
  selectVisualizationDataByKey,
  selectVisualizationDataLoading,
} from '../../../../state/visualizationData/visualizationDataReducer'
import {
  ButtonWrapper,
  ChartContainer,
  ChartWrapper,
  DisabledOverlay,
  MessageContainer,
  NowWrapper,
  PatientDetailsWrapper,
  RangePickerContainer,
  RootContainer,
  StyledButton,
  TimeScaleWrapper,
} from './styles'
import { VisualizationDataEntityType } from '../../../../state/visualizationData/types'
import CustomLegend from './CustomLegend'
import { getVisualizationDataFile } from '../../../../state/visualizationData/api'
import { VisualizationCsvDataType } from '../../../../types/SensorType'
import { DownloadOutlined } from '@ant-design/icons'

const { RangePicker } = DatePicker

export const getPriorityLabel = (value: number): string => {
  switch (value) {
    case 0:
      return 'MET Call'
    case 1:
      return 'Urgent Assessment'
    case 2:
      return 'Critical Care'
    default:
      return ''
  }
}

const initialTime: TimeType = {
  startTime: dayjs(Date.now()).subtract(1, 'hour'),
  endTime: dayjs(Date.now()),
}

const PatternTab: React.FC<PatternTabPropsType> = ({
  patientKey,
  patientId,
  patientFirstName,
  patientLastName,
}) => {
  console.log('Rendering...', Date.now().valueOf())
  const [time, setTime] = useState<TimeType>({ ...initialTime })

  const dispatch = useDispatch()
  const isLoading = useSelector(selectVisualizationDataLoading)
  const selectVisualizationData: VisualizationDataEntityType | undefined =
    useSelector(selectVisualizationDataByKey(patientKey))
  console.log(selectVisualizationData)
  const [timeSliderKey, setTimeSliderKey] = useState<number>(0)
  const [rangePickerValue, setRangePickerValue] =
    useState<RangeValue<dayjs.Dayjs>>()

  const [radioValue, setRadioValue] = useState<number>(
    getMillisecondsFromHour(1)
  )
  const [isSelectTimeSlider, setIsSelectTimeSlider] = useState(true)

  let yTickList: number[] = []
  let dataPoints: DataPointType[] = []
  let uniquePatterns: string[] = []
  let patternList: string[] = [...PATTERNS]
  let colorList: string[] = [...COLORS]
  let svgPatternMap: Map<string, string[]> = new Map()

  const generatePattern = (pattern: string) => {
    if (!svgPatternMap.get(pattern)) {
      const patternIndex = Math.floor(Math.random() * patternList.length)
      const colorIndex = Math.floor(Math.random() * colorList.length)
      const patternId = `pattern${patternIndex}`
      const svgPattern =
        patternList.length === 0
          ? undefined
          : patternList.splice(patternIndex, 1)
      const svgColor =
        colorList.length === 0 ? ['#FF0000'] : colorList.splice(colorIndex, 1)
      if (svgPattern && svgColor) {
        svgPatternMap.set(pattern, [patternId, svgPattern[0], svgColor[0]])
      }
    }
    return svgPatternMap.get(pattern)?.[0]
  }

  if (
    selectVisualizationData &&
    selectVisualizationData.visualizationData.length != 0
  ) {
    dataPoints = [...selectVisualizationData.visualizationData]
      .sort((a, b) => b.time - a.time)
      .slice(0, 6)
      .map((point) => ({
        svgPattern: generatePattern(point.pattern),
        pattern: point.pattern,
        time: point.time,
        rank: point.rank,
        priority:
          PRIORITY_VALUES[
            point.clinicalAction as keyof typeof PRIORITY_VALUES
          ] ?? 0,
      }))

    uniquePatterns = Array.from(svgPatternMap.values()).map((value) => value[0])

    const timeRange = time.endTime.valueOf() - time.startTime.valueOf()
    yTickList = Array.from(
      { length: 11 },
      (_, i) => time.startTime.valueOf() + (i * timeRange) / 10
    )
  }

  useEffect(() => {
    dispatch(
      getVisualizationData({
        patientKey: patientKey,
        startTime: time.startTime.valueOf(),
        endTime: time.endTime.valueOf(),
      })
    )
  }, [time, patientKey])

  const updateTimeSelection = useCallback(
    (startHour: number, endHour: number) => {
      const newStartTime = dayjs(Date.now()).subtract(startHour, 'hour')
      const newEndTime = dayjs(Date.now()).subtract(endHour, 'hour')
      setTime({ startTime: newStartTime, endTime: newEndTime })
    },
    []
  )

  const rangePickerOnChange = useCallback((value: RangeValue<dayjs.Dayjs>) => {
    if (!value) {
      resetRangePicker()
    } else {
      const startTime = value[0]!.valueOf()
      const endTime = value[1]!.valueOf()
      const differenceInHours = dayjs(endTime).diff(dayjs(startTime), 'hour')
      if (differenceInHours > 24) {
        setRangePickerValue(undefined)
        alert('Please select a range that is no more than 24 hours.')
        return
      }

      setRangePickerValue(value)
      setTime({ startTime: dayjs(startTime), endTime: dayjs(endTime) })
      setRadioValue(getMillisecondsFromHour(0))
    }
  }, [])

  const resetRangePicker = () => {
    const startTime = new Date().getTime() - getMillisecondsFromHour(1)
    const endTime = new Date().getTime()

    setRangePickerValue(undefined)
    setRadioValue(getMillisecondsFromHour(1))
    setTime({ startTime: dayjs(startTime), endTime: dayjs(endTime) })
  }

  function getMillisecondsFromHour(hour: number) {
    return hour * 60 * 60 * 1000
  }

  const applyRangePickerEvent = (value: RangeValue<dayjs.Dayjs>) => {
    setRadioValue(getMillisecondsFromHour(0))
    setRangePickerValue(value)
  }

  const getVisualizationFilename = () => {
    const startRange = time.startTime.format('DD-MM-YY-HH-mm-ss')
    const endRange = time.endTime.format('DD-MM-YY-HH-mm-ss')
    const fileName = `${patientLastName}_${patientFirstName}_Vitals_${startRange}_${endRange}`
    return fileName
  }

  const handleDownoad = useCallback(async () => {
    const { data } = await getVisualizationDataFile(
      patientKey,
      time.startTime.valueOf(),
      time.endTime.valueOf()
    )
    if (!data) return
    const formattedData = data.visualizationData
    const convertedToCSV = convertToCSV(formattedData)
    if (convertedToCSV === '') return
    const blob = new Blob([convertedToCSV])
    const fileName = `${getVisualizationFilename()}.csv`
    downloadFileFromBinaryData(blob, fileName)
  }, [time, patientKey])

  const convertToCSV = (data: VisualizationCsvDataType[]): string => {
    if (data.length === 0) {
      message.info('No data to be downloaded')
      return ''
    }

    const updatedData = data.map(item => {
      const scores = [item.spo2Score, item.prScore, item.respScore, item.tempScore, item.bpScore]
      const totalMews = scores.reduce((sum, score) => sum + (score === -1 ? 0 : score), 0);
      return {
        total_mews: totalMews === 0 ? '0' : totalMews,
        spo2Score: item.spo2Score === 0 ? '0' : item.spo2Score,
        spo2: item.spo2 === 0 ? '0' : item.spo2,
        prScore: item.prScore === 0 ? '0' : item.prScore,
        pr: item.pr === 0 ? '0' : item.pr,
        respScore: item.respScore === 0 ? '0' : item.respScore,
        resp: item.resp === 0 ? '0' : item.resp,
        tempScore: item.tempScore === 0 ? '0' : item.tempScore,
        temp: item.temp === 0 ? '0' : item.temp,
        bpScore: item.bpScore === 0 ? '0' : item.bpScore,
        dia: item.dia === 0 ? '0' : item.dia,
        sys: item.sys === 0 ? '0' : item.sys,
        createTimestamp: item.createTimestamp
      };
    });

    updatedData.sort((a, b) => {
      return (new Date(b.createTimestamp)).getTime() - (new Date(a.createTimestamp)).getTime();
    });

    const headers = Object.keys(updatedData[0]).filter(
      (header) => !['createTimestamp', 'patientKey'].includes(header)
    )
    headers.push('createTimestamp')

    const csvRows = [
      headers.join(','),
      ...updatedData.map((row) =>
        headers
          .map(
            (header) =>
              `"${(row[header as keyof typeof row] || '').toString().replace(/"/g, '\\"')}"`
          )
          .join(',')
      ),
    ]

    return csvRows.join('\n')
  }

  function downloadFileFromBinaryData(
    blob: Blob | MediaSource,
    fileName: string
  ) {
    const downloadLink = document.createElement('a')
    downloadLink.href = URL.createObjectURL(blob)
    downloadLink.download = fileName
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
  }

  return (
    <RootContainer>
      {Array.from(svgPatternMap).map(([key, pattern]: [string, string[]]) => (
        <svg width='0' height='0' viewBox='0 0 100 100' key={key}>
          <defs>
            <pattern
              id={pattern[0]}
              width={7}
              height={7}
              patternUnits='userSpaceOnUse'
            >
              <svg
                width={7}
                height={7}
                viewBox='0 0 100 100'
                preserveAspectRatio='none'
              >
                <path
                  d={pattern[1]}
                  stroke={pattern[2]}
                  strokeWidth='15'
                  fill='white'
                />
              </svg>
            </pattern>
          </defs>
        </svg>
      ))}
      <TimeScaleWrapper>
        <ButtonWrapper>
          <StyledButton
            isActive={isSelectTimeSlider}
            disabled={false}
            onClick={() =>
              setIsSelectTimeSlider((prevState) => {
                setTime({ ...initialTime })
                setTimeSliderKey((prevState) => prevState + 1)
                return true
              })
            }
          >
            Last 24 hrs Time Slider
          </StyledButton>
          <StyledButton
            isActive={!isSelectTimeSlider}
            disabled={false}
            onClick={() =>
              setIsSelectTimeSlider((prevState) => {
                resetRangePicker()
                return false
              })
            }
          >
            Time Range Picker
          </StyledButton>
          <StyledButton
            isActive={true}
            disabled={isLoading}
            onClick={handleDownoad}
            style={{ width: '50px' }}
          >
            <DownloadOutlined />
          </StyledButton>
        </ButtonWrapper>
        <PatientDetailsWrapper>
          Patient Name: <strong>{`${patientFirstName} ${patientLastName}`}</strong>
          <br />
          Patient ID: <strong>{patientId}</strong>
        </PatientDetailsWrapper>
        {!isSelectTimeSlider && (
          <DisabledOverlay
            left='15%'
            width='40%'
            height='110px'
          ></DisabledOverlay>
        )}
        <TimeScaleSlider
          key={timeSliderKey}
          startHour={1}
          endHour={0}
          updateTimeSelection={updateTimeSelection}
        />
        {isSelectTimeSlider && (
          <DisabledOverlay
            left='55%'
            width='45%'
            height='110px'
          ></DisabledOverlay>
        )}
        <RangePickerContainer>
          <label className='controlLabel'> Time Range:</label>
          <RangePicker
            locale={{
              ...enUS,
              lang: {
                ...enUS.lang,
                ok: 'Apply',
              },
            }}
            value={rangePickerValue}
            showTime
            allowClear
            disabledDate={(current) => {
              const [start, end] = rangePickerValue || []
              const isFuture = current.isAfter(dayjs(), 'day')

              if (!start && !end) {
                return isFuture
              }

              if (start) {
                const minEndDate = dayjs(start)
                const maxEndDate = dayjs(start).add(1, 'days')
                return (
                  current.isBefore(minEndDate, 'day') ||
                  current.isAfter(maxEndDate, 'day') ||
                  isFuture
                )
              }

              if (end) {
                const minStartDate = dayjs(end).subtract(1, 'days')
                return (
                  current.isBefore(minStartDate, 'day') ||
                  current.isAfter(dayjs(end), 'day') ||
                  isFuture
                )
              }

              return isFuture
            }}
            disabledTime={(current, type) => {
              if (type === 'start' && current) {
                const currentDate = dayjs()
                const selectedDate = dayjs(current)
                if (selectedDate.isSame(currentDate, 'day')) {
                  return {
                    disabledHours: () =>
                      Array.from({ length: 24 }, (_, i) => i).slice(
                        currentDate.hour() + 1
                      ),
                    disabledMinutes: () => [currentDate.minute(), 59],
                    disabledSeconds: () => [currentDate.second(), 59],
                  }
                }
              }
              return {}
            }}
            onOk={applyRangePickerEvent}
            format='DD/MM/YYYY HH:mm:ss'
            onChange={rangePickerOnChange}
          />
        </RangePickerContainer>
      </TimeScaleWrapper>

      <PatientDetailsWrapper></PatientDetailsWrapper>
      {isLoading ? (
        <MessageContainer>{MESSAGE_LOADING}</MessageContainer>
      ) : !isSelectTimeSlider && rangePickerValue === undefined ? (
        <MessageContainer>{MESSAGE_TIMERANGE}</MessageContainer>
      ) : dataPoints.length === 0 ? (
        <MessageContainer>{MESSAGE_NO_DATA}</MessageContainer>
      ) : (
        <ChartContainer>
          <ChartWrapper>
            {time.endTime.valueOf() >
              dayjs(Date.now()).subtract(30, 'minutes').valueOf() && (
              <NowWrapper>Now</NowWrapper>
            )}

            <ResponsiveContainer width='100%' height={500}>
              <ScatterChart
                margin={{
                  top: 20,
                  right: 20,
                  bottom: 20,
                  left: 20,
                }}
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey='priority'
                  type='number'
                  domain={[-0.5, 2.5]}
                  tickFormatter={(value) => getPriorityLabel(value)}
                  axisLine={false}
                  tickLine={false}
                  ticks={[0, 1, 2]}
                  label={{
                    value: 'Priority',
                    offset: -15,
                    position: 'insideBottom',
                    style: { fontWeight: 'bold', fontSize: '14px' },
                  }}
                />
                <ReferenceArea
                  x1={-0.5}
                  x2={0.5}
                  strokeOpacity={0.3}
                  fill='#9966FF'
                />
                <ReferenceArea
                  x1={0.5}
                  x2={1.5}
                  strokeOpacity={0.3}
                  fill='#FFCC66'
                />
                <ReferenceArea
                  x1={1.5}
                  x2={2.5}
                  strokeOpacity={0.3}
                  fill='#FFFF99'
                />
                <YAxis
                  dataKey='time'
                  tickFormatter={(timestamp) =>
                    dayjs(timestamp).format('HH:mm')
                  }
                  domain={[time.startTime.valueOf(), time.endTime.valueOf()]}
                  tickCount={10}
                  ticks={[...yTickList]}
                  padding={{ bottom: 20 }}
                  label={{
                    value: 'Time (24 Hour Format)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontWeight: 'bold', fontSize: '14px' },
                  }}
                />
                <ZAxis
                  type='number'
                  dataKey='rank'
                  range={[2000, 400]}
                  name='rank'
                />
                <Tooltip content={<CustomTooltip />} />
                {uniquePatterns.map((svgPattern) => (
                  <Scatter
                    key={svgPattern}
                    name={`Points (${svgPattern})`}
                    data={dataPoints.filter(
                      (point) => point.svgPattern === svgPattern
                    )}
                    fill={`url(#${svgPattern})`}
                    shape='circle'
                  />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
          </ChartWrapper>
          <CustomLegend
            payload={uniquePatterns.map((pattern) => ({ value: pattern }))}
            svgPatternMap={svgPatternMap}
          />
        </ChartContainer>
      )}
    </RootContainer>
  )
}

export default React.memo(PatternTab)
